import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { HousingApplication, HousingCriteriaResponse, IncomeDeclaration } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const STATES = [
  'APPLIED',
  'AUTO_VERIFY',
  'SCORED',
  'QUEUED',
  'ROUND_ELIGIBLE',
  'COMMITTEE',
  'ALLOCATED',
  'TENANCY',
  'PURCHASE',
  'WAITLISTED',
  'REJECTED',
  'WITHDRAWN',
] as const;

export function HousingApplications() {
  const [district, setDistrict] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (district) params.set('district', district);
    if (state) params.set('state', state);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/housing/applications?${params.toString()}`;
  }, [district, state, q]);
  const list = useCursorList<HousingApplication>(basePath);
  const criteriaQ = useApiQuery<HousingCriteriaResponse>('/api/v1/housing/criteria');

  return (
    <div>
      <PageHeader
        title="Housing Applications"
        subtitle="Application -> income verification -> published-formula score -> live district queue position."
      />

      {criteriaQ.data ? (
        <div className="card mb-5 p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Published scoring formula (max {criteriaQ.data.maxScore} points)</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {criteriaQ.data.criteria.map((c) => (
              <div key={c.code} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="text-xs font-semibold text-slate-700">{c.label}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{c.rule ?? c.note}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="District" className="w-44">
          <input className="field-input" placeholder="e.g. Anse Royale" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="State" className="w-48">
          <select className="field-input" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All states</option>
            {STATES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Search NIN" className="min-w-[200px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[700px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">NIN</th>
              <th className="table-head">District</th>
              <th className="table-head">Household</th>
              <th className="table-head">Score</th>
              <th className="table-head">State</th>
              <th className="table-head">Applied</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((a) => (
              <tr key={a.id} className="table-row-link" onClick={() => navigate(`/housing/applications/${a.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{a.nin}</td>
                <td className="table-cell">{a.districtPreference}</td>
                <td className="table-cell">{a.householdSize}</td>
                <td className="table-cell tabular-nums">{a.score ?? '—'}</td>
                <td className="table-cell"><StatusBadge status={a.state} /></td>
                <td className="table-cell text-xs text-slate-500">{formatDate(a.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading applications…" /> : null}
        {!list.loading && list.items.length === 0 ? (
          <EmptyState title="No applications found" hint="Citizen submission happens through the public housing portal (out of scope here) — see the build report." />
        ) : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />
    </div>
  );
}

export function HousingApplicationDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('HOUSING_OFFICER') ?? false;
  const appQ = useApiQuery<HousingApplication & { incomeDeclaration: IncomeDeclaration | null; queuePosition: number | null }>(`/api/v1/housing/applications/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [attestedAmount, setAttestedAmount] = useState('');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      appQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (appQ.loading) return <Spinner label="Loading application…" />;
  if (appQ.error || !appQ.data) return <ErrorNote message={appQ.error ?? 'Application not found'} />;
  const app = appQ.data;
  const decl = app.incomeDeclaration;
  const isTerminal = ['ALLOCATED', 'TENANCY', 'PURCHASE', 'REJECTED', 'WITHDRAWN'].includes(app.state);

  return (
    <div>
      <BackLink to="/housing/applications" />
      <PageHeader title={app.nin} subtitle={`District: ${app.districtPreference} · household of ${app.householdSize}`} actions={<StatusBadge status={app.state} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Household &amp; declared circumstances</h2>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Current housing</dt><dd className="font-medium text-slate-800">{app.currentHousing.replaceAll('_', ' ')}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Special needs</dt><dd className="font-medium text-slate-800">{app.specialNeeds.length > 0 ? app.specialNeeds.join(', ') : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Declared monthly income</dt><dd className="font-medium text-slate-800">{scr(app.declaredMonthlyIncome)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Verified monthly income</dt><dd className="font-medium text-slate-800">{app.verifiedMonthlyIncome !== null ? scr(app.verifiedMonthlyIncome) : '—'}</dd></div>
            </dl>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Household composition</div>
              <ul className="space-y-0.5 text-xs text-slate-600">
                {app.householdComposition.map((m, i) => (
                  <li key={i}>{m.relation} — DOB {m.dob}{m.nin ? ` · NIN ${m.nin}` : ''}</li>
                ))}
              </ul>
            </div>
          </div>

          {decl ? (
            <div className="card p-5">
              <h2 className="mb-3 text-sm font-bold text-slate-700">Income declaration</h2>
              <dl className="mb-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Source</dt><dd className="font-medium text-slate-800">{decl.source.replaceAll('_', ' ')}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Declared amount</dt><dd className="font-medium text-slate-800">{scr(decl.declaredAmount)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Status</dt><dd><StatusBadge status={decl.status} /></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Evidence</dt><dd className="font-mono text-xs text-slate-500">{decl.evidenceDocRef} ({decl.evidenceSha256.slice(0, 10)}…)</dd></div>
              </dl>
              {isOfficer && decl.status === 'PENDING' ? (
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <Field label="Attested amount (optional override, SCR)">
                    <input className="field-input" type="number" min="0" value={attestedAmount} onChange={(e) => setAttestedAmount(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button
                      className="btn-success flex-1"
                      disabled={busy}
                      onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/income/attest`, { approve: true, attestedAmount: attestedAmount ? Number(attestedAmount) : undefined }), 'Income declaration attested.')}
                    >
                      Attest (approve)
                    </button>
                  </div>
                  <Field label="Rejection reason (required to reject)">
                    <input className="field-input" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger w-full"
                    disabled={busy || !rejectionReason}
                    onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/income/attest`, { approve: false, rejectionReason }), 'Income declaration rejected.')}
                  >
                    Reject declaration
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {app.scoreBreakdown ? (
            <div className="card p-5">
              <h2 className="mb-3 text-sm font-bold text-slate-700">Score breakdown (base {app.scoreBreakdown.baseScore} / {app.scoreBreakdown.maxScore})</h2>
              <div className="space-y-2">
                {app.scoreBreakdown.criteria.map((c) => (
                  <div key={c.code} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-800">{c.label}</div>
                      <div className="text-xs text-slate-500">{c.note}</div>
                    </div>
                    <div className="font-bold tabular-nums text-slate-800">{c.points}/{c.maxPoints}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-5">
          {app.queuePosition !== null && app.queuePosition !== undefined ? (
            <StatCard label="Live queue position" value={`#${app.queuePosition}`} hint={app.districtPreference} tone="good" />
          ) : null}

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Officer actions</h2>
            {!isOfficer ? <p className="text-xs text-slate-400">Requires HOUSING_OFFICER.</p> : null}
            <div className="space-y-2">
              {isOfficer && app.state === 'APPLIED' ? (
                <button className="btn-primary w-full" disabled={busy || decl?.status !== 'ATTESTED'} onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/verify`), 'Income verified.')}>
                  Verify income {decl?.status !== 'ATTESTED' ? '(attest first)' : ''}
                </button>
              ) : null}
              {isOfficer && app.state === 'AUTO_VERIFY' ? (
                <button className="btn-primary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/score`), 'Score computed.')}>
                  Compute score
                </button>
              ) : null}
              {isOfficer && app.state === 'SCORED' ? (
                <button className="btn-primary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/enqueue`), 'Enqueued.')}>
                  Enqueue (join live district queue)
                </button>
              ) : null}
              {isOfficer && !isTerminal ? (
                <button className="btn-danger w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/applications/${id}/withdraw`), 'Application withdrawn.')}>
                  Withdraw
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
